import { useEffect, useRef, useState } from 'react';
import {
  recordKeysIgnored,
  recordRejectedBurst,
  recordScannerAttached
} from './scanDiagnostics';
import { WedgeAssembler, characterFromKeyEvent } from './wedgeAssembler';
import type { WedgeAssemblerOptions, WedgeScan } from './wedgeAssembler';

/**
 * Listens for a handheld barcode scanner — a Zebra device running DataWedge, or any USB/Bluetooth
 * HID scanner — for as long as a scanning screen is open.
 *
 * Two channels are wired up at once, because which one a device uses is a setting on the device
 * rather than something the app gets to choose:
 *
 *   - **Key events on the document.** What Zebra recommends for web apps ("Send Characters as
 *     Events"). Needs no focused field, and is also how every HID scanner behaves, so this half
 *     works on the desktop too.
 *   - **A hidden capture field.** DataWedge's factory default delivers the barcode as a *string*
 *     committed into whatever field has focus, so with nothing focused the scan goes nowhere at
 *     all. Keeping a one-pixel input focused is what makes an unconfigured device work out of
 *     the box, and it is also the channel that most reliably carries the GS separators between
 *     GS1 application identifiers.
 *
 * The field has a cost — focusing an input on Android can raise the soft keyboard — and the
 * obvious guard against that, `inputmode="none"`, is self-defeating: it tells Chrome the field
 * wants no input method, and the input method is exactly how a wedge commits a scan as a string.
 * Rather than keep trying to have both, the field is created **only in laser mode**, where there
 * is no camera preview for a keyboard to cover and where the string channel is the whole point.
 * Camera mode runs on key events alone, which is all a Bluetooth HID scanner ever needed.
 *
 * The field also never takes focus from one the operator is typing in, so manual entry is
 * untouched.
 */

export type HardwareScannerStatus = {
  /** A scanning screen is open, so the listener is live. */
  armed: boolean;
  /** At least one hardware scan has arrived in this session. */
  detected: boolean;
};

const CAPTURE_FIELD_PREFERENCE = 'rxerp.hardwareScanner.captureField';
const SUPPRESS_KEYBOARD_PREFERENCE = 'rxerp.hardwareScanner.suppressKeyboard';

/** Focus can be lost to a tap, a modal closing or a re-render; this quietly takes it back. */
const REFOCUS_INTERVAL_MS = 750;
/*
 * There was a delay here, on the theory that Chrome only raises the keyboard for a programmatic
 * focus made inside a user gesture's task. Tested on the device: the keyboard still opened. The
 * activation Chrome checks is *sticky* — set by the first interaction with the page and never
 * cleared — so once the operator has tapped anything, every later focus can raise it, whatever
 * task it runs in. Deferring bought nothing and cost a third of a second of not listening.
 *
 * Focusing a field is the only thing that ever asks for a keyboard, so the only reliable cure is
 * not to focus one: `?wedgefield=off`, which is safe once the wedge sends key events.
 */

const readPreference = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private browsing and locked-down WebViews throw rather than return null.
    return null;
  }
};

/**
 * A rugged handheld terminal rather than a phone or tablet — Zebra, Honeywell, Datalogic and the
 * rest put their model number in the user agent.
 *
 * Used only to pick the *default* scan mode, where being wrong costs one tap on the mode switch.
 * It is deliberately kept out of anything that decides how a scan is captured: a Zebra whose
 * model this regex did not recognise was once handed the one field configuration that cannot
 * receive a scan, and the failure was silent.
 */
export const isHandheldTerminal = (): boolean => {
  const ua = navigator.userAgent || '';
  return (
    /zebra|symbol|datalogic|honeywell|intermec|unitech|cipherlab|point ?mobile|memor|skorpio|falcon|dolphin/i.test(
      ua
    ) || /\b(TC|MC|ET|EC|WT|VC|PS|CC|HC|CT|CK|CN|EDA|PM|RS|RK|MT|HT)\d{2}/.test(ua)
  );
};

/**
 * Reads a preference that can also be set from the URL, so it can be changed on a device in the
 * warehouse rather than only over USB debugging. `?wedgefield=off` writes the preference and then
 * survives the navigation the next scan performs, exactly as `?scandebug=1` does.
 */
const readUrlBackedPreference = (key: string, param: string): string | null => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(param);
    if (fromUrl === 'on' || fromUrl === 'off') {
      window.localStorage.setItem(key, fromUrl);
      return fromUrl;
    }
  } catch {
    /* a URL or storage we cannot read simply means the stored value */
  }
  return readPreference(key);
};

/**
 * Whether this platform can hold a focused capture field at all.
 *
 * The caller decides *whether one is wanted* — only laser mode is — and this decides whether the
 * platform can be trusted with it. Only iOS is excluded: it ignores `inputmode` and would raise a
 * keyboard, and every iOS scanner is a Bluetooth HID one that sends key events anyway.
 *
 * `?wedgefield=off` (or `rxerp.hardwareScanner.captureField`) turns it off for a device whose
 * keyboard will not stay down. Safe **only** where the wedge sends key events — with "Send
 * Characters as Events" enabled the field is redundant, and without it there is nowhere for a
 * scan to land and scanning stops entirely.
 */
export const shouldUseCaptureField = (): boolean => {
  const override = readUrlBackedPreference(CAPTURE_FIELD_PREFERENCE, 'wedgefield');
  if (override === 'on') return true;
  if (override === 'off') return false;
  return !/iPad|iPhone|iPod/i.test(navigator.userAgent || '');
};

/**
 * Whether to put `inputmode="none"` on the capture field. **Off by default, and it should stay
 * that way.**
 *
 * The attribute suppresses the on-screen keyboard, but it also tells Chrome the field wants no
 * input method — and the input method is exactly how a wedge commits a scan in its default string
 * mode. Setting it is therefore the difference between a handheld that scans and one that
 * silently does not, and it has now caused that outage twice: once applied by device class, once
 * applied at runtime by a viewport heuristic.
 *
 * It is no longer needed for its original purpose either. The capture field is only created in
 * laser mode, where there is no camera preview for a keyboard to cover; camera mode does not
 * create one at all. The override remains only for a device that somehow needs it.
 */
export const shouldSuppressKeyboard = (): boolean =>
  readPreference(SUPPRESS_KEYBOARD_PREFERENCE) === 'on';

const NON_TEXT_INPUT_TYPES = [
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'file'
];

const isEditable = (element: Element | null): boolean => {
  if (!element || !(element instanceof HTMLElement)) return false;
  if (element.isContentEditable) return true;
  const tag = element.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  // Checkboxes, radios and buttons are not places an operator types, so a scan should not be
  // held hostage by one of them having focus.
  return !NON_TEXT_INPUT_TYPES.includes((element as HTMLInputElement).type);
};

const CAPTURE_FIELD_SELECTOR = '[data-hardware-scanner-capture]';

/**
 * True for any capture field, not merely the one this attach created.
 *
 * A field left behind by an earlier attach still holds focus and still looks editable, so the
 * guard that protects manual entry would treat it as the operator's own and drop every key event
 * into it — scanning would stop dead while the screen looked fine. Recognising ours by attribute
 * rather than by identity makes that unfalsifiable.
 */
const isCaptureField = (element: Element | null): boolean =>
  Boolean(element && element.matches?.(CAPTURE_FIELD_SELECTOR));

const createCaptureField = (suppressKeyboard: boolean) => {
  // Never two. An orphan from an attach whose teardown did not run would hold focus and swallow
  // the scans meant for this one.
  document.querySelectorAll(CAPTURE_FIELD_SELECTOR).forEach((stale) => stale.remove());
  const field = document.createElement('input');
  field.type = 'text';
  field.setAttribute('data-hardware-scanner-capture', 'true');
  field.setAttribute('autocomplete', 'off');
  field.setAttribute('autocorrect', 'off');
  field.setAttribute('autocapitalize', 'off');
  field.setAttribute('spellcheck', 'false');
  // Decouples focus from the on-screen keyboard *without* touching the input method, which is
  // the distinction that matters: `inputmode="none"` hides the keyboard by telling Chrome the
  // field wants no input method at all, and the input method is the channel a wedge uses to
  // commit a scan as a string. This attribute leaves the field a normal IME target and only
  // stops the keyboard being raised automatically on focus. Chromium-only; ignored elsewhere,
  // which is a safe no-op.
  //
  // It shipped once before and was reverted, but never on its own: it went out alongside logic
  // that retired the capture field after a scan arrived on key events alone, and that logic
  // retired the field on any label without separators in it — a zone or tote label — persisting
  // the decision. On a device whose wedge delivers strings that removes the only place a scan
  // can land, permanently. That was the outage; this was along for the ride.
  field.setAttribute('virtualkeyboardpolicy', 'manual');
  // See `shouldSuppressKeyboard`: this hides the on-screen keyboard but also tells Chrome the
  // field wants no input method, which is the channel a wedge uses to commit a scan as a string.
  if (suppressKeyboard) field.setAttribute('inputmode', 'none');
  field.setAttribute('aria-label', 'Handheld barcode scanner input');
  field.tabIndex = -1;
  field.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'width:1px',
    'height:1px',
    'padding:0',
    'border:0',
    'outline:0',
    // Not display:none or visibility:hidden — those cannot hold focus.
    'opacity:0',
    'z-index:-1',
    'pointer-events:none',
    'caret-color:transparent',
    // Anything under 16px makes iOS zoom the page on focus.
    'font-size:16px'
  ].join(';');
  document.body.appendChild(field);
  return field;
};

export type AttachHardwareScannerOptions = {
  onScan: (scan: WedgeScan) => void;
  /** Overrides the platform decision about the capture field. */
  useCaptureField?: boolean;
  /** Overrides the platform decision about `inputmode="none"`. */
  suppressKeyboard?: boolean;
  /** Timing overrides, for tests. */
  assembler?: Partial<WedgeAssemblerOptions>;
};

/**
 * Wires the listeners up to the live document and returns the teardown. Split out of the hook so
 * the DOM behaviour — which event goes to which channel, and who is allowed to hold focus — can
 * be exercised against real events rather than through a component.
 */
export const attachHardwareScanner = ({
  onScan,
  useCaptureField,
  suppressKeyboard,
  assembler: assemblerOptions
}: AttachHardwareScannerOptions): (() => void) => {
  const wantsCaptureField =
    useCaptureField === undefined ? shouldUseCaptureField() : useCaptureField;
  const suppressingKeyboard = suppressKeyboard ?? shouldSuppressKeyboard();
  const captureField: HTMLInputElement | null = wantsCaptureField
    ? createCaptureField(suppressingKeyboard)
    : null;
  let disposed = false;
  // The listener is re-attached on every navigation to a scanning screen, and a scan that lands
  // between a detach and the next attach goes nowhere. Logging the lifecycle makes that visible
  // instead of leaving "it worked once" to be guessed at.
  recordScannerAttached({
    captureField: Boolean(captureField),
    suppressingKeyboard
  });

  const assembler = new WedgeAssembler({
    onScan,
    // Empty the field after every burst, accepted or not, so a rejected one cannot leave its
    // characters behind for the next scan to be appended to.
    onBurstEnd: () => {
      if (captureField) captureField.value = '';
    },
    // A burst the assembler threw away leaves no other trace, and "nothing happened" is the
    // hardest handheld symptom to diagnose without one.
    onBurstResult: recordRejectedBurst,
    ...assemblerOptions
  });

  /**
   * Takes focus back unless the operator is typing. Called on a timer as well as on events,
   * because focus is lost in ways that do not always announce themselves — a modal unmounting, a
   * re-render replacing a node, the page being restored from the back/forward cache.
   *
   * Only editable elements are left alone. Buttons are not: Chrome for Android focuses a button
   * when it is tapped, and refusing to take focus back would mean the trigger stopped working
   * until the operator happened to touch something else — which is the whole feature. The cost is
   * that a dialog's Esc-to-close and Tab trap go quiet on the screens that scan; on a handheld
   * with no keyboard that is not a trade worth making the other way.
   */
  const ensureFocus = () => {
    if (disposed || !captureField) return;
    const active = document.activeElement;
    if (active === captureField) return;
    // An orphaned capture field is not the operator typing, so take focus back from it.
    if (isEditable(active) && !isCaptureField(active)) return;
    captureField.focus({ preventScroll: true });
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const target = event.target as Element | null;
    // While the operator has a real field focused, their keystrokes — and any scan they aim at
    // that field — belong to it. Manual entry has to keep working.
    if (target !== captureField && !isCaptureField(target) && isEditable(target)) {
      // Deliberate — a barcode aimed at the manual-entry box belongs to that box. But it is also
      // indistinguishable from scanning being broken, so leave a trace of what took the keys.
      if (event.key.length === 1) recordKeysIgnored(target);
      return;
    }

    if (event.key === 'Enter' || event.key === 'NumpadEnter' || event.key === 'Tab') {
      if (!assembler.isAssembling) return;
      event.preventDefault();
      assembler.pushTerminator(event.key === 'Tab' ? 'tab' : 'enter');
      return;
    }

    const character = characterFromKeyEvent(event);
    if (character === null) return;
    assembler.pushKey(character);
  };

  const handleBeforeInput = (event: Event) => {
    if (event.target !== captureField) return;
    const inputEvent = event as InputEvent;
    const kind = inputEvent.inputType;
    const INSERTIONS = [
      'insertText',
      'insertFromPaste',
      'insertCompositionText',
      'insertReplacementText'
    ];
    if (kind && !INSERTIONS.includes(kind)) return;
    // `beforeinput` carries the text verbatim, which is what survives when the browser's value
    // sanitisation would otherwise strip something we need.
    if (inputEvent.data) assembler.pushFieldInsert(inputEvent.data);
  };

  const handleInput = (event: Event) => {
    if (event.target !== captureField || !captureField) return;
    assembler.pushFieldValue(captureField.value);
  };

  const handleCompositionEnd = (event: Event) => {
    if (event.target !== captureField) return;
    const { data } = event as CompositionEvent;
    if (data) assembler.pushFieldInsert(data);
  };

  const handlePaste = (event: ClipboardEvent) => {
    const target = event.target as Element | null;
    if (target !== captureField && !isCaptureField(target) && isEditable(target)) return;
    const text = event.clipboardData?.getData('text');
    if (!text) return;
    if (target === captureField) event.preventDefault();
    assembler.pushAtomic(text, 'paste');
  };

  const handleFocusOut = () => {
    // Deferred: focus is momentarily nowhere while it moves between elements, and grabbing it
    // back inside the event would beat the element that is about to receive it.
    window.setTimeout(ensureFocus, 0);
  };

  const handleVisibility = () => {
    if (document.visibilityState === 'visible') ensureFocus();
  };

  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('paste', handlePaste, true);
  document.addEventListener('focusout', handleFocusOut, true);
  document.addEventListener('visibilitychange', handleVisibility);
  window.addEventListener('focus', ensureFocus);
  if (captureField) {
    captureField.addEventListener('beforeinput', handleBeforeInput);
    captureField.addEventListener('input', handleInput);
    captureField.addEventListener('compositionend', handleCompositionEnd);
  }

  ensureFocus();
  const refocusTimer = window.setInterval(ensureFocus, REFOCUS_INTERVAL_MS);

  return () => {
    disposed = true;
    window.clearInterval(refocusTimer);
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('paste', handlePaste, true);
    document.removeEventListener('focusout', handleFocusOut, true);
    document.removeEventListener('visibilitychange', handleVisibility);
    window.removeEventListener('focus', ensureFocus);
    if (captureField) {
      captureField.removeEventListener('beforeinput', handleBeforeInput);
      captureField.removeEventListener('input', handleInput);
      captureField.removeEventListener('compositionend', handleCompositionEnd);
      captureField.blur();
      captureField.remove();
    }
    assembler.dispose();
  };
};

export type UseHardwareScannerOptions = {
  enabled: boolean;
  onScan: (scan: WedgeScan) => void;
  /**
   * Whether this screen wants the focused capture field. Only laser mode does: it is the channel
   * a wedge in string mode needs, and it is also the only thing that can raise a keyboard, so
   * camera mode leaves it off and keeps the preview clear.
   */
  useCaptureField?: boolean;
};

export const useHardwareScanner = ({
  enabled,
  onScan,
  useCaptureField
}: UseHardwareScannerOptions): HardwareScannerStatus => {
  const [detected, setDetected] = useState(false);
  // Held in a ref so a caller passing an inline callback does not tear the listeners — and the
  // capture field's focus — down and rebuild them on every render.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return undefined;
    return attachHardwareScanner({
      useCaptureField,
      onScan: (scan) => {
        setDetected(true);
        onScanRef.current(scan);
      }
    });
  }, [enabled, useCaptureField]);

  return { armed: enabled, detected };
};

export default useHardwareScanner;
